"use client";

import React, { useEffect, useState, useRef } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "motion/react";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Camera,
  Hotel,
} from "lucide-react";
import Slider from "react-slick";
import { createLookupObject, cleanHeaderTags } from "@/_helper/helper";
import { useAppSelector } from "@/_hooks/hooks";
import { ICMS } from "@/store/homeSlice";

interface IGalleryItem {
  ID: number;
  Image: string;
  HotelName: string;
  GroupName: string;
}

interface IGalleryGroup {
  GroupName: string;
  Images: IGalleryItem[];
}

interface GalleryProps {
  cmsData: ICMS[];
  galleryData: IGalleryGroup[];
}

export default function GalleryClient({ cmsData, galleryData }: GalleryProps) {
  const { loading } = useAppSelector((state) => state.hotel);
  const [lookUpCMSData, setLookUpCMSData] = useState<Record<string, any>>({});
  const BannerList = useAppSelector((state) => state.home.BannerList);
  const banner: any =
    BannerList?.find((banner: any) => banner?.BannerType === "gallery") || {};

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [initialSlideIndex, setInitialSlideIndex] = useState(0);
  const [nav1, setNav1] = useState<Slider | null>(null);
  const [nav2, setNav2] = useState<Slider | null>(null);
  const slider1 = useRef<Slider>(null);
  const slider2 = useRef<Slider>(null);

  useEffect(() => {
    if (isModalOpen) {
      setNav1(slider1.current);
      setNav2(slider2.current);
      document.body.style.overflow = "hidden";
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") closeModal();
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        document.body.style.overflow = "unset";
        window.removeEventListener("keydown", handleKeyDown);
      };
    } else {
      document.body.style.overflow = "unset";
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (cmsData && cmsData.length > 0) {
      setLookUpCMSData(createLookupObject(cmsData, "Name"));
    }
  }, [cmsData]);

  const [currentImages, setCurrentImages] = useState<any[]>([]);

  const openImageModal = (images: any[], index: number) => {
    setCurrentImages(images);
    setInitialSlideIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const groupedGallery = galleryData.reduce((acc: any, item: any) => {
    const key = item.HotelName || "Other";

    if (!acc[key]) acc[key] = [];
    acc[key].push(item);

    return acc;
  }, {});

  const hotelNames = Object.keys(groupedGallery);
  const [activeHotel, setActiveHotel] = useState(hotelNames[0]);

  const mainSliderSettings = {
    asNavFor: nav2!,
    ref: slider1,
    initialSlide: initialSlideIndex,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    fade: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
  };

  const thumbSliderSettings = {
    asNavFor: nav1!,
    ref: slider2,
    initialSlide: initialSlideIndex,
    slidesToShow: 6,
    swipeToSlide: true,
    focusOnSelect: true,
    infinite: true,
    centerMode: true,
    arrows: false,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 5 } },
      { breakpoint: 768, settings: { slidesToShow: 4 } },
      { breakpoint: 480, settings: { slidesToShow: 3 } },
    ],
  };

  return (
    <>
      <main className="min-h-screen bg-[#FCFBF9] text-gray-900 font-sans">
        {/* Hero Section */}
        <section className="relative h-[50vh] overflow-hidden">
          {banner?.BannerImage && (
            <Image
              src={banner?.BannerImage || ""}
              alt={banner?.BannerTitle || "Banner Image"}
              fill
              className="object-cover"
              priority
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 via-gray-900/0 to-gray-900/0 pointer-events-none"></div>

          <div className="absolute inset-0 flex items-end pointer-events-none pb-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                {banner?.SubTitle && (
                  <div className="inline-flex items-center gap-4 px-5 py-2 bg-white/10 backdrop-blur-xl border border-white/20 rounded-full mb-8">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
                      <span className="w-2 h-2 rounded-full bg-white/80" />
                      <span className="w-2 h-2 rounded-full bg-green-600 animate-pulse" />
                    </div>
                    <p className="text-white text-xs font-bold tracking-[0.4em] uppercase">
                      {banner?.SubTitle}
                    </p>
                  </div>
                )}
                <h1 className="text-5xl md:text-7xl font-bold text-amber-300 mb-6 drop-shadow-2xl">
                  {banner?.BannerTitle || "Our Gallery"}
                </h1>
                {banner?.Description && (
                  <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto drop-shadow-lg">
                    {banner?.Description}
                  </p>
                )}
              </motion.div>
            </div>
          </div>
        </section>

        <div className="sticky top-20 z-40 bg-white/80 backdrop-blur-2xl border-b border-gray-100 shadow-sm">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex overflow-x-auto gap-4 py-6 no-scrollbar items-center">
              <div className="p-2 bg-blue-50 rounded-lg text-blue-600 mr-2 flex-shrink-0">
                <Hotel size={20} />
              </div>
              {hotelNames?.map((hotel) => (
                <button
                  key={hotel}
                  onClick={() => setActiveHotel(hotel)}
                  className={`px-8 py-2.5 rounded-full font-bold whitespace-nowrap transition-all text-sm tracking-wide border-2 ${
                    activeHotel === hotel
                      ? "bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow-xl scale-105 border-orange-400"
                      : "bg-white text-gray-600 border-gray-100 hover:border-orange-200 hover:bg-orange-50/30"
                  }`}
                >
                  {hotel}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Gallery Content Section */}
        <section className="py-24 relative z-20">
          <div className="container mx-auto px-6">
            {/* Section Heading */}
            <div className="text-center mb-16">
              {lookUpCMSData?.["Gallery - Section 1 : Title"] ? (
                <div
                  className="flex flex-col items-center blog-content"
                  dangerouslySetInnerHTML={{
                    __html:
                      cleanHeaderTags(lookUpCMSData?.["Gallery - Section 1 : Title"]?.Template),
                  }}
                />
              ) : (
                <div className="flex flex-col items-center">
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-3 mb-4"
                  >
                    <div className="h-[2px] w-12 bg-orange-500 rounded-full"></div>
                    <p className="text-blue-600 font-bold tracking-widest uppercase text-sm">
                      Curated Gallery
                    </p>
                    <div className="h-[2px] w-12 bg-green-600 rounded-full"></div>
                  </motion.div>
                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    className="text-4xl md:text-6xl font-bold text-gray-900 mb-8 font-playfair"
                  >
                    Immersive{" "}
                    <span className="text-amber-500">Perspectives</span>
                  </motion.h2>
                  <p className="text-gray-500 text-lg max-w-3xl mx-auto leading-relaxed font-light">
                    Every picture tells a story of unity, history, and
                    architectural brilliance at the Statue of Unity.
                  </p>
                </div>
              )}
            </div>

            {/* <div className="flex flex-wrap justify-center gap-3 mb-10">
              {hotelNames.map((hotel) => (
                <button
                  key={hotel}
                  onClick={() => setActiveHotel(hotel)}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                    activeHotel === hotel
                      ? "bg-orange-500 text-white shadow-lg"
                      : "bg-gray-100 text-gray-600 hover:bg-orange-100"
                  }`}
                >
                  {hotel}
                </button>
              ))}
            </div> */}

            {/* Unified Image Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {groupedGallery[activeHotel]?.map((img: any, idx: number) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: (idx % 4) * 0.1 }}
                  onClick={() =>
                    openImageModal(groupedGallery[activeHotel], idx)
                  }
                  className="group relative aspect-[4/3] rounded-[2rem] overflow-hidden cursor-pointer shadow-xl hover:shadow-2xl transition-all duration-500"
                >
                  <Image
                    src={img.Image}
                    alt={`${img?.HotelName || "Gallery Image"}`}
                    fill
                    className="object-cover transition-transform duration-1000 group-hover:scale-110"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 flex flex-col items-center justify-end p-8">
                    <div className="mb-4 translate-y-10 group-hover:translate-y-0 transition-transform duration-500 flex flex-col items-center">
                      <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center shadow-lg border border-white/20 mb-3">
                        <Maximize2 className="text-white" size={20} />
                      </div>
                      <p className="text-white font-bold text-lg mb-1 tracking-tight">
                        {img?.HotelName}
                      </p>
                      <span className="text-orange-200 text-xs font-bold tracking-widest uppercase flex items-center gap-2">
                        <Camera size={12} /> View Capture
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Modal Lightbox */}
        <AnimatePresence>
          {isModalOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[999999] bg-black/90 backdrop-blur-xl flex flex-col overflow-hidden"
            >
              {/* Header */}
              <div className="flex justify-between items-center px-4 sm:px-8 py-4 sm:py-6 text-white absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 via-black/30 to-transparent">
                <div className="flex items-center gap-4 sm:gap-6">
                  <div className="border-l-4 border-orange-500 pl-4 sm:pl-6">
                    <h3 className="text-lg sm:text-2xl font-bold tracking-tight">
                      {
                        // galleryData[initialSlideIndex]?.HotelName ||
                        "Gallery View"
                      }
                    </h3>
                    <p className="text-xs text-gray-400 tracking-widest uppercase font-bold mt-0.5 sm:mt-1">
                      Statue of Unity Visual Tour
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeModal}
                  className="bg-white/10 hover:bg-orange-600 p-2.5 sm:p-3 rounded-full backdrop-blur-lg transition-all group border border-white/10 flex-shrink-0"
                  aria-label="Close gallery"
                >
                  <X
                    className="text-white group-hover:rotate-90 transition-transform duration-500 w-5 h-5 sm:w-6 sm:h-6"
                  />
                </button>
              </div>

              {/* Sliders Area */}
              <div className="flex-1 flex flex-col mt-20 justify-center items-center p-4 md:p-8 w-full max-w-[1400px] mx-auto overflow-hidden">
                {/* Main Slider */}
                <div className="w-full relative px-6 md:px-20 mb-8 max-h-[75vh]">
                  <Slider
                    {...mainSliderSettings}
                    className="w-full gallery-main-slider"
                  >
                    {currentImages?.map((img: any, idx: number) => (
                      <div key={idx} className="relative outline-none">
                        <div className="relative w-full h-[65vh] md:h-[70vh]">
                          <Image
                            src={img.Image}
                            alt={img?.HotelName || "Full Gallery Image"}
                            fill
                            className="object-contain"
                            priority
                            sizes="100vw"
                          />
                        </div>
                      </div>
                    ))}
                  </Slider>
                </div>

                {/* Thumbnails Slider */}
                <div className="w-full max-w-5xl px-6 h-20 md:h-24">
                  <Slider {...thumbSliderSettings} className="thumb-nav-slider">
                    {currentImages?.map((img: any, idx: number) => (
                      <div
                        key={idx}
                        className="px-2 py-2 focus:outline-none cursor-pointer group"
                      >
                        <div className="relative aspect-video rounded-2xl overflow-hidden border-2 border-transparent transition-all opacity-30 [.slick-current_&]:opacity-100 [.slick-current_&]:border-orange-500 [.slick-current_&]:scale-110 group-hover:opacity-70 group-hover:scale-105">
                          <Image
                            src={img.Image}
                            alt={`${img?.HotelName} Thumbnail`}
                            fill
                            className="object-cover"
                            sizes="200px"
                          />
                        </div>
                      </div>
                    ))}
                  </Slider>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      <style jsx global>{`
        .section-title h2 {
          font-size: 3.5rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
          color: #1a1a1a;
          font-family: "Playfair Display", serif;
        }
        .section-title p {
          color: #666;
          font-size: 1.25rem;
          font-weight: 300;
          max-width: 45rem;
          margin: 0 auto;
        }
        .gallery-main-slider .slick-slide {
          padding: 0 10px;
        }
        .thumb-nav-slider .slick-track {
          display: flex;
          align-items: center;
        }
      `}</style>
    </>
  );
}

// Custom Navigation Arrows for Slick Slider
function NextArrow({ onClick }: any) {
  return (
    <button
      onClick={onClick}
      className="absolute right-[-20px] md:right-[-40px] top-1/2 -translate-y-1/2 z-20 bg-white/10 hover:bg-orange-600 p-3 rounded-full text-white backdrop-blur-xl transition-all border border-white/10 flex items-center justify-center group"
    >
      <ChevronRight
        size={30}
        className="group-hover:translate-x-1 transition-transform"
      />
    </button>
  );
}

function PrevArrow({ onClick }: any) {
  return (
    <button
      onClick={onClick}
      className="absolute left-[-20px] md:left-[-40px] top-1/2 -translate-y-1/2 z-20 bg-white/10 hover:bg-orange-600 p-3 rounded-full text-white backdrop-blur-xl transition-all border border-white/10 flex items-center justify-center group"
    >
      <ChevronLeft
        size={30}
        className="group-hover:-translate-x-1 transition-transform"
      />
    </button>
  );
}
